import type {IdentifyResponse, Roof, SearchLocation} from "../react-app-env";
import * as ol from "openlayers";
import * as proj4 from "proj4";
import {
    getWmts,
    searchAllRoofsFromBuildingId,
    searchBestRoofFromBuildingId,
    searchFeaturesFromCoord,
    searchGeocode,
} from "./geoAdminService";
import {
    getErrorMsg,
    getForViewAndSize,
    getToleranceInPixels,
} from "../utils/map";
import { setProjections } from "../utils/mapProjections";

(window as any).proj4 = proj4;
setProjections(proj4);


export const featuresFromCoord = async (
    map: ol.Map,
    coord: [number, number],
    tolerance: number,
    lang: string
): Promise<IdentifyResponse> => {
    const view = map.getView();
    let mapExtent = view.calculateExtent(map.getSize());

    // Ensure resolution is not too high
    if (view.getResolution() && view.getResolution() > 20.0) {
        mapExtent = getForViewAndSize(coord, 20.0, 0.0, map.getSize());
    }

    const pixelTolerance = Math.max(
        getToleranceInPixels(tolerance, mapExtent, map.getSize()),
        1
    );

    return await searchFeaturesFromCoord(map, coord, mapExtent, pixelTolerance, lang);
};

export const navigateTo = (
    map: ol.Map,
    dest: [number, number],
    destRes: number,
    instantView: boolean,
): void => {
    const view = map.getView();
    const source = view.getCenter()!;
    const sourceRes = view.getResolution()!;
    const dist = Math.hypot(source[0] - dest[0], source[1] - dest[1]);
    const duration = instantView ? 2 : Math.min(Math.sqrt(300 + (dist / sourceRes) * 1000), 3000);

    view.animate({ center: dest, duration, easing: ol.easing.easeOut });

    if (dist > 1000 || sourceRes !== destRes) {
        view.animate(
            {
                resolution: Math.max(sourceRes, dist / 1000, destRes * 1.2),
                duration: duration / 2,
                easing: (ol.easing as any).easeInOut,
            },
            {
                resolution: destRes,
                duration: duration / 2,
                easing: (ol.easing as any).bounce,
            }
        );
    }
};

export const createTileLayer = (
    layerId: string,
    layersConfig: Record<string, any>,
    defaultOpacity = 1
): ol.layer.Tile =>
    new ol.layer.Tile({
        minResolution: layersConfig[layerId]?.minResolution,
        maxResolution: layersConfig[layerId]?.maxResolution,
        opacity: layersConfig[layerId]?.opacity ?? defaultOpacity,
        source: getWmts(layerId, layersConfig[layerId]),
        useInterimTilesOnError: false,
    });

export const onMapMoveHandler = (
    event: any,
    map: ol.Map,
    targetLayer: ol.layer.Layer
): void => {
    const isHoverLayer = map.forEachLayerAtPixel(
        event.pixel,
        () => true,
        undefined,
        (layer) => layer === targetLayer
    );
    (map.getTargetElement() as any).style.cursor = isHoverLayer ? "pointer" : "";
};

export const onMapClickHandler = async (
    event: any,
    map: ol.Map,
    lang: string,
    handleAddress: (address: any, roof: Roof | null, searchAddress: SearchLocation | null) => void
): Promise<void> => {
    const coord = event.coordinate;
    const data = await featuresFromCoord(map, coord, 3.0, lang);
    const roofResult = data.results[0];

    // Skip if no roof found or only a perimeter marker (no actual roof data)
    if (!roofResult || roofResult.perimeter !== undefined) {
        return;
    }

    onRoofFound(map, roofResult, false, async (bestRoof) => {
        const mapExtent = map.getView().calculateExtent(map.getSize());
        const pixelTolerance = getToleranceInPixels(100.0, mapExtent, map.getSize());
        const geocodeData = await searchGeocode(map, coord, pixelTolerance, mapExtent);
        if (geocodeData.results[0]) {
            let attributes = geocodeData.results[0].attributes;
            const street = attributes.strname_deinr;
            const zip = attributes.dplz4;
            const newAddress: SearchLocation = {
                id: 0,
                label: street + ' ' + zip + ' ' + attributes.dplzname,
                detail: '',
                lat: 0,
                lon: 0,
                zoomLevel: 0,
                objectClass: '',
                origin: 'address',
                rank: 0,
                cleanLabel: street + ' ' + zip + ' ' + attributes.dplzname,
                x: coord[1],
                y: coord[0],
            }
            handleAddress(geocodeData.results[0], bestRoof, newAddress);
        }
    });
};

export const onMapDoubleClickHandler = async (
    event: any,
    map: ol.Map,
    lang: string,
    handleAllRoofs: (address: any, roofs: any[], searchAddress: SearchLocation | null) => void
): Promise<void> => {
    const coord = event.coordinate;
    const data = await featuresFromCoord(map, coord, 3.0, lang);
    const roofResult: any = data.results[0];

    if (!roofResult || roofResult.perimeter !== undefined) {
        return;
    }

    const buildingId = roofResult.attributes?.building_id;
    if (!buildingId) return;

    // Fetch ALL roof surfaces for this building
    const allRoofs = await searchAllRoofsFromBuildingId(buildingId);
    if (!allRoofs.length) return;

    // Get geocode for address
    const mapExtent = map.getView().calculateExtent(map.getSize());
    const pixelTolerance = getToleranceInPixels(100.0, mapExtent, map.getSize());
    const geocodeData = await searchGeocode(map, coord, pixelTolerance, mapExtent);

    if (geocodeData.results[0]) {
        const attributes = geocodeData.results[0].attributes;
        const street = attributes.strname_deinr;
        const zip = attributes.dplz4;
        const newAddress: SearchLocation = {
            id: 0,
            label: street + ' ' + zip + ' ' + attributes.dplzname,
            detail: '',
            lat: 0,
            lon: 0,
            zoomLevel: 0,
            objectClass: '',
            origin: 'address',
            rank: 0,
            cleanLabel: street + ' ' + zip + ' ' + attributes.dplzname,
            x: coord[1],
            y: coord[0],
        };
        handleAllRoofs(geocodeData.results[0], allRoofs, newAddress);
    }
};

export const onAddressFound = async (
    map: ol.Map,
    address: any,
    lang: string,
    handleAddress: (address: any, roof: Roof | null, searchAddress: SearchLocation | null) => void
): Promise<void> => {
    if (!address) {
        handleAddress(null, null, null);
        return;
    }

    try {
        const coord: [number, number] = address.geometry
            ? [address.geometry.x, address.geometry.y]
            : [address.y, address.x];

        // Zoom to address immediately so the map moves even if roof lookup is slow
        navigateTo(map, coord, 0.25, false);

        const data = await featuresFromCoord(map, coord, 0.0, lang);
        const roof: any = data.results[0];

        onRoofFound(map, roof, true, async (bestRoof) => {
            try {
                const mapExtent = roof?.bbox || map.getView().calculateExtent(map.getSize());
                const pixelTolerance = getToleranceInPixels(100.0, mapExtent, map.getSize());
                const geocodeData = await searchGeocode(map, coord, pixelTolerance, mapExtent);

                handleAddress(geocodeData.results[0], bestRoof, address);
            } catch (innerErr) {
                console.error("onAddressFound geocode failed:", innerErr);
                handleAddress(null, bestRoof, address);
            }
        });
    } catch (err) {
        console.error("onAddressFound features lookup failed:", err);
    }
};

export const onRoofFound = (
    map: ol.Map,
    roof: Roof | null,
    findBestRoof: boolean,
    callback: (roof: Roof | null) => void
): void => {
    if (roof?.perimeter === undefined && findBestRoof && roof?.attributes?.building_id) {
        searchBestRoofFromBuildingId(roof.attributes.building_id).then(callback);
    } else {
        callback(roof);
    }
};

export const clearVectorLayer = (
    borderLayer: ol.layer.Vector
): void => {
    borderLayer.getSource()?.clear();
};

export const createVectorLayers = () => ({
    borderLayer: new ol.layer.Vector({
        source: new ol.source.Vector(),
        style: [
            // Outer glow / halo for contrast against any background
            new ol.style.Style({
                stroke: new ol.style.Stroke({
                    color: [255, 255, 255, 0.7],
                    width: 5,
                }),
            }),
            // Main border + fill
            new ol.style.Style({
                stroke: new ol.style.Stroke({
                    color: [15, 95, 41, 0.9],
                    width: 3,
                }),
                fill: new ol.style.Fill({
                    color: [76, 255, 41, 0.3],
                }),
            }),
        ],
    }),
});

export const updateRoofInfo = (
    map: ol.Map,
    borderLayer: ol.layer.Vector,
    roof: any,
    instantView: boolean,
): void => {
    if (!roof?.geometry?.rings) return;
    const polygon = new ol.geom.Polygon(roof?.geometry?.rings);
    const center = polygon.getInteriorPoint().getCoordinates();
    borderLayer.getSource()?.addFeature(new ol.Feature(polygon));

    navigateTo(map, center, 0.25, instantView);
};

export const getGeoLocation = (
    map: ol.Map,
    handleAddress: (address: any, roof: any, searchAddress: any) => void,
    onError: (message: string) => void
): void => {
    if (!navigator.geolocation) {
        onError(getErrorMsg());
        return;
    }

    navigator.geolocation.getCurrentPosition(
        async (position) => {
            const coord21781 = ol.proj.transform(
                [position.coords.longitude, position.coords.latitude],
                "EPSG:4326",
                map.getView().getProjection()
            );
            const mapExtent = map.getView().calculateExtent(map.getSize());
            const pixelTolerance = getToleranceInPixels(100.0, mapExtent, map.getSize());
            const geocodeData = await searchGeocode(map, coord21781, pixelTolerance, mapExtent);

            handleAddress(geocodeData.results[0], null, null);
        },
        (error) => onError(getErrorMsg(error))
    );
};
